import type { StatusTone } from "../../formatting/status";
import { computeShowingRange } from "../../formatting/pagination";

/**
 * Pure, no React/I/O derivations for the Contacts redesign
 * (REDISENO-PANEL.md §7). Same "independent feature slice, no cross-feature
 * import" precedent `campaignDetailDerived.ts`/`telemetry/derived.ts` already
 * established -- these are contacts-only concerns (avatar hashing, status
 * tone, suppression motivo labels), never shared with campaigns/telemetry.
 */

export interface ContactStatusPresentation {
  label: string;
  tone: StatusTone;
}

/** Mirrors the `ContactStatus` enum presentation (unknown values degrade to an `info` tone with their raw text, never a misleading color). */
export function resolveContactStatus(estado: string): ContactStatusPresentation {
  switch (estado) {
    case "Activo":
      return { label: "Activo", tone: "positive" };
    case "Suprimido":
      return { label: "Suprimido", tone: "warning" };
    case "EnNegociacion":
      return { label: "En negociación", tone: "info" };
    default:
      return { label: estado, tone: "info" };
  }
}

/** `CLevel` -> "C-Level" (REDISENO-PANEL.md §7.1); every other `HierarchyLevel` is already a human-readable word. */
export function formatNivelLabel(nivel: string): string {
  return nivel === "CLevel" ? "C-Level" : nivel;
}

/** "cargo · nivel" meta line under the company name (§7.1 row) / under the header name (§7.2). */
export function formatCargoNivel(cargo: string, nivelLabel: string): string {
  return `${cargo} · ${nivelLabel}`;
}

/** "cargo · empresa" header subtitle (§7.2). */
export function formatCargoEmpresa(cargo: string, empresa: string): string {
  return `${cargo} · ${empresa}`;
}

/** `contact.shows` ("Show_Europa, jhamf_prueba") split into a trimmed, non-empty name list -- `[]` when the contact belongs to no Show. */
export function parseShowNames(shows: string | null): string[] {
  if (!shows) return [];
  return shows
    .split(",")
    .map((name) => name.trim())
    .filter(Boolean);
}

/**
 * Deterministic initials for the avatar circle: first letter of the first
 * word + first letter of the last word (uppercase); a single-word name
 * yields just that one letter; an empty/blank name degrades to "?" rather
 * than throwing or rendering nothing.
 */
export function initialsFor(nombre: string): string {
  const words = nombre.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return "?";
  if (words.length === 1) return words[0]!.charAt(0).toUpperCase();
  return (words[0]!.charAt(0) + words[words.length - 1]!.charAt(0)).toUpperCase();
}

export interface AvatarColors {
  bg: string;
  fg: string;
}

/**
 * 5 fg/bg pairs (REDISENO-PANEL.md §7.1 "5 pares de color asignados por hash
 * del id"). Deliberately NOT the exact §2 status-pill hues: reusing those
 * verbatim would (a) make a purely cosmetic avatar color look like a status
 * signal, and (b) two of them (`#059669`/`#ecfdf5` green, `#dc2626`/`#fef2f2`
 * red) measure only 3.58:1 / 4.41:1 -- below WCAG AA's 4.5:1 for normal text,
 * found by computing real contrast ratios rather than trusting the palette
 * by eye (same lesson as T3 Codex finding 12). Red is also dropped on
 * purpose: §2's hierarchy rule reserves red for destructive actions/error
 * figures, never a neutral avatar. Every pair below is pinned in
 * `derived.test.ts` against `formatting/contrast.ts`, not merely asserted.
 */
export const AVATAR_COLOR_PAIRS: readonly AvatarColors[] = [
  { bg: "#eff6ff", fg: "#1d4ed8" },
  { bg: "#ecfdf5", fg: "#047857" },
  { bg: "#f5f3ff", fg: "#6d28d9" },
  { bg: "#fffbeb", fg: "#b45309" },
  { bg: "#fce7f3", fg: "#be185d" },
];

/** Simple deterministic string hash (sum of char codes) -- stable across renders/sessions for the same `contactId`, no crypto needed for a purely cosmetic assignment. */
function hashString(value: string): number {
  let hash = 0;
  for (let index = 0; index < value.length; index += 1) {
    hash = (hash + value.charCodeAt(index)) | 0;
  }
  return Math.abs(hash);
}

/** Picks one of the 5 `AVATAR_COLOR_PAIRS` deterministically from a contact's id. */
export function avatarColorsFor(contactId: string): AvatarColors {
  return AVATAR_COLOR_PAIRS[hashString(contactId) % AVATAR_COLOR_PAIRS.length]!;
}

/** `ck_suppression_motivo` closed vocabulary (sql/migrations/001_schema_inicial.sql) -- the one place both the Suprimir modal's select AND the suppression-history/notice's human label come from, so they can never drift apart. */
export const SUPPRESSION_MOTIVOS = [
  { value: "opt-out", label: "Opt-out (solicitó no recibir más)" },
  { value: "negociacion", label: "En negociación" },
  { value: "rebote_duro", label: "Rebote duro" },
  { value: "queja_spam", label: "Queja de spam" },
] as const;

/** Motivos only the system assigns (never offered in the manual suppression form). */
const SYSTEM_SUPPRESSION_MOTIVO_LABELS: Readonly<Record<string, string>> = {
  filtrado_spam: "Filtrado como spam",
};

/** A legible label for a stored `motivo` code; an unrecognized/legacy value degrades to its own raw text rather than disappearing. */
export function suppressionMotivoLabel(motivo: string): string {
  return (
    SUPPRESSION_MOTIVOS.find((option) => option.value === motivo)?.label ??
    SYSTEM_SUPPRESSION_MOTIVO_LABELS[motivo] ??
    motivo
  );
}

export interface AuditEntryPresentation {
  /** "suprimir" (amber) / "revertir" (green) dot color token. */
  dotColor: string;
  text: string;
}

/** Mirrors the prototype's timeline dot colors (§7.2: "círculo ámbar = suprimir, verde = revertir") and the "Suprimido · {motivo legible}" / "Supresión revertida" text. */
export function formatAuditEntry(entry: { accion: "suprimir" | "revertir"; motivo: string | null }): AuditEntryPresentation {
  if (entry.accion === "suprimir") {
    return {
      dotColor: "#b45309",
      text: `Suprimido · ${entry.motivo ? suppressionMotivoLabel(entry.motivo) : "motivo no especificado"}`,
    };
  }
  return { dotColor: "#059669", text: "Supresión revertida" };
}

/**
 * "Mostrando a–b de total" (§7.1 footer) -- 1-based inclusive range, `0–0`
 * for a genuinely empty page, a non-positive size, a page before the first,
 * or a page past the last (clamped, never inverted). RDD
 * (review-reliability, WARNING, R3-002): this used to be its own private
 * copy of `components/Pagination.tsx`'s identical (and identically
 * incomplete) math -- both now share `formatting/pagination.ts`'s single
 * implementation so they can never drift apart again.
 */
export const formatShowingRange = computeShowingRange;
